import React, { useCallback, useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import { useSaveToast } from '../ui/SaveToast';
import LabeledToggle from '../ai/LabeledToggle';
import './clientInvitations.css';

// BL-177 Step 2m: Admin Center › Client invitations.
//   PLATFORM switch (platform admins): "Send invitations to new contacts", default ON.
//   COMPANY switch (company admins), shown UNDER it: can only be STRICTER. The database ANDs the two (portal_invitations_enabled):
//     if the platform has invitations off, the company switch is grey and says so.
// What it controls: the "create your profile" link that ends a reservation confirmation for a contact with no client-app profile yet.
// Off means no link is added. (The Invite button on a contact card is a person's deliberate action and is not affected.)
const FAILED_PREFIX = "Couldn't save. Switched back.";

export default function ClientInvitationsSettings({ orgId, isPlatformSuperAdmin = false, client = supabaseClient }) {
    const toast = useSaveToast();
    const [status, setStatus] = useState(null);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        const { data, error: loadError } = await client.rpc('portal_invitations_status');
        if (loadError) { setError(loadError.message || 'Could not load the invitation switches.'); return; }
        setStatus(data);
        setError('');
    }, [client]);
    useEffect(() => { load(); }, [load]);

    async function save(apply, revert, write) {
        apply();
        try { await write(); toast.saved(); await load(); } catch (failure) { revert(); toast.failed(`${FAILED_PREFIX} ${failure?.message || ''}`.trim()); }
    }
    function setPlatform(next) {
        const before = status;
        return save(
            () => setStatus((current) => ({ ...current, platform: next, enabled: next && current.company })),
            () => setStatus(before),
            async () => {
                const { error: writeError } = await client.from('portal_invitations_platform_settings').update({ send_invitations_to_new_contacts: next }).eq('singleton', true);
                if (writeError) throw writeError;
            },
        );
    }
    function setCompany(next) {
        const before = status;
        return save(
            () => setStatus((current) => ({ ...current, company: next, enabled: current.platform && next })),
            () => setStatus(before),
            async () => {
                const { error: writeError } = await client.from('org_portal_invitation_settings').upsert([{ org_id: orgId, send_invitations: next }], { onConflict: 'org_id' });
                if (writeError) throw writeError;
            },
        );
    }

    if (error) return <div role="alert" className="rounded-lg border border-red-500/50 bg-red-500/10 p-4 text-sm text-[var(--ink)]">{error}</div>;
    if (!status) return <div className="p-6 text-[var(--ink-muted)]">Loading…</div>;

    return (
        <div className="max-w-2xl space-y-4" data-testid="client-invitations-settings">
            <p className="text-sm text-[var(--ink-muted)]">
                When a reservation is confirmed for someone who has no client-app profile yet, the confirmation ends with a link to confirm their
                details and create their profile. Contacts you add by hand get it with their first confirmation. Imports never send invitations.
            </p>
            <section className="ia-d2 ia-d2-card space-y-4 rounded-xl p-4" aria-label="Invitation switches">
                <LabeledToggle
                    label="Send invitations to new contacts"
                    hint={isPlatformSuperAdmin ? 'Platform switch: applies to every company. Default on.' : 'Platform switch (only platform admins change this).'}
                    checked={status.platform === true}
                    disabled={!isPlatformSuperAdmin}
                    onChange={setPlatform}
                />
                <div className="ml-4 border-l-2 border-[#33415E] pl-4">
                    <LabeledToggle
                        label="Send invitations from your company"
                        hint={status.platform === true
                            ? 'Your switch can only be stricter than the platform: turn it off to add no link to your confirmations.'
                            : 'Greyed out: invitations are switched off for the whole platform.'}
                        checked={status.company === true && status.platform === true}
                        disabled={status.platform !== true}
                        onChange={setCompany}
                    />
                </div>
                <p role="status" className="text-sm font-bold text-white" data-testid="invitations-effective">
                    Right now: {status.enabled ? 'invitation links are added to confirmations.' : 'no invitation links are added.'}
                </p>
            </section>
        </div>
    );
}
